import type { TabsProps } from 'antd';
import {
    AppstoreOutlined,
    ShoppingCartOutlined,
    LineChartOutlined,
    DollarOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Form, type FormInstance, Tabs } from 'antd';
import { useEffect, useState } from 'react';
import type { LogFilter } from '@/features/log/types/Log';
import type { FormServicoValues } from '@/features/servico/types/Servico';
import { useEmpresaConfiguracaoQuery } from '@/features/empresa/api/apiEmpresaConfiguracaoGet';
import { OrigemEnum } from '@/features/log/types/enum';
import { useFilter } from '@/std/hooks/useFilter';
import { ServicoTabKeyHistoricoAlteracao } from '../useServicoTabItems';
import { ServicoTabHistoricoAlteracaoCusto } from './ServicoTabHistoricoAlteracaoCusto';
import { ServicoTabHistoricoAlteracaoCustoMedio } from './ServicoTabHistoricoAlteracaoCustoMedio';
import { ServicoTabHistoricoAlteracaoCustoVenda } from './ServicoTabHistoricoAlteracaoCustoVenda';
import { ServicoTabHistoricoAlteracaoGeral } from './ServicoTabHistoricoAlteracaoGeral';

function isServicoTabKey(value: string): value is ServicoTabKeyHistoricoAlteracao {
    return Object.values(ServicoTabKeyHistoricoAlteracao).includes(
        value as ServicoTabKeyHistoricoAlteracao,
    );
}

export function ServicoTabHistoricoAlteracao({ form }: { form: FormInstance<FormServicoValues> }) {
    const navigate = useNavigate({ from: '/a/servico/$action/{-$idpk}' });

    const { tab2 } = useSearch({ from: '/a/servico/$action/{-$idpk}' }) as {
        tab2: ServicoTabKeyHistoricoAlteracao;
    };

    const { empresaConfig } = useEmpresaConfiguracaoQuery();

    const { filter, setFilter } = useFilter<LogFilter>();
    const pro_idpk = Form.useWatch('pro_idpk', form);

    const [enabled, setEnabled] = useState<boolean>(false);

    useEffect(() => {
        if (pro_idpk) {
            setFilter({
                log_origem: OrigemEnum.Produto,
                log_origem_idpk: pro_idpk,
            });
        }
    }, [pro_idpk, setFilter]);

    useEffect(() => {
        if (filter?.log_origem_idpk) {
            setEnabled(
                tab2 === ServicoTabKeyHistoricoAlteracao.Geral &&
                    !!pro_idpk &&
                    !!filter?.log_origem_idpk,
            );
        }
    }, [filter, pro_idpk, tab2]);

    const items: TabsProps['items'] = [];

    if (empresaConfig?.emc_log_gerar === 'S') {
        items.push({
            children: <ServicoTabHistoricoAlteracaoGeral enabled={enabled} filter={filter} />,
            icon: <AppstoreOutlined />,
            key: 'geral',
            label: 'Geral',
        });
    }

    items.push(
        {
            children: <ServicoTabHistoricoAlteracaoCusto form={form} tab2={tab2} />,
            icon: <ShoppingCartOutlined />,
            key: 'preco-custo',
            label: 'Preço de custo',
        },
        {
            children: <ServicoTabHistoricoAlteracaoCustoMedio form={form} tab2={tab2} />,
            icon: <LineChartOutlined />,
            key: 'custo-medio',
            label: 'Custo médio',
        },
        {
            children: <ServicoTabHistoricoAlteracaoCustoVenda form={form} tab2={tab2} />,
            icon: <DollarOutlined />,
            key: 'preco-venda',
            label: 'Preço de venda',
        },
    );

    return (
        <Tabs
            activeKey={tab2}
            items={items}
            onChange={(k) => {
                if (!isServicoTabKey(k)) {
                    return;
                }

                void navigate({
                    search: (prev) => ({
                        ...prev,
                        tab2: k,
                    }),
                });
            }}
        />
    );
}
